import { State } from '@meraki-internal/state';
import { APIClient } from '@innerhive-internal/innerhive-api-client';
import { OrganizationsState } from '../OrganizationsState';
import { IOrganization } from '../IOrganization';
import { Logger } from '../../support/Logger';

interface IDemoDataRefreshProgress {
    isRunning: boolean;
    done: number;
    total: number;
    failed: number;
    /** Clients past the last storyline, whom the api leaves with no demo data. */
    withoutStoryline: number;
}

interface IDemoDataRefreshResult {
    storylineKey: string | null;
}

// Each client is several dozen writes, so a few at once gets through a caseload quickly without a
// burst big enough to throttle.
const CONCURRENT_CLIENTS = 4;

/**
 * Re-seeds every client of a demo organization with its invented record, dated back from now. The
 * api takes one client per request, so a caseload stays inside the request time limit and this can
 * say how far it has got.
 */
export class DemoDataRefreshState extends State<IDemoDataRefreshProgress> {
    static inject = () => [APIClient, OrganizationsState, Logger];
    constructor(
        private apiClient: APIClient,
        private orgState: OrganizationsState,
        private logger: Logger
    ) {
        super({ isRunning: false, done: 0, total: 0, failed: 0, withoutStoryline: 0 });
    }

    refresh = async (org: IOrganization): Promise<void> => {
        const link = org.links.demoData;
        if (!link || this.state.isRunning) {
            return;
        }
        // Claimed before the caseload loads, so a second press during the load cannot start a second run.
        this.setState({ isRunning: true, done: 0, total: 0, failed: 0, withoutStoryline: 0 });

        try {
            await this.orgState.loadOrganizationCareMaps(org.orgId);
        } catch (error) {
            this.setState({ isRunning: false });
            throw error;
        }
        const pending = [...this.orgState.getOrganizationCareMaps(org.orgId)];
        this.setState({ total: pending.length });

        const work = async () => {
            for (let careMap = pending.shift(); careMap; careMap = pending.shift()) {
                try {
                    const result: IDemoDataRefreshResult = await this.apiClient.post(link, {}, undefined, { userId: careMap.userId, careMapId: careMap.careMapId });
                    if (result.storylineKey === null) {
                        this.setState({ withoutStoryline: this.state.withoutStoryline + 1 });
                    }
                } catch (error) {
                    // One client failing should not stop the rest.
                    this.logger.error(error, { context: 'demo data refresh failed for one client', careMapId: careMap.careMapId });
                    this.setState({ failed: this.state.failed + 1 });
                }
                this.setState({ done: this.state.done + 1 });
            }
        };

        try {
            await Promise.all(Array.from({ length: CONCURRENT_CLIENTS }, work));
        } finally {
            this.setState({ isRunning: false });
        }
    };
}
